/**
 * Toast context for managing toast notifications throughout the app
 */
import { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Toast from '../components/ui/Toast';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(toast => {
    const id = toast.id || `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast = { ...toast, id };

    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods for different toast types
  const toast = useCallback(
    (message, options = {}) => {
      return addToast({
        type: 'info',
        message,
        ...options,
      });
    },
    [addToast],
  );

  const success = useCallback(
    (message, options = {}) => {
      return addToast({
        type: 'success',
        message,
        ...options,
      });
    },
    [addToast],
  );

  const error = useCallback(
    (message, options = {}) => {
      return addToast({
        type: 'error',
        message,
        duration: 7000, // Longer duration for errors
        ...options,
      });
    },
    [addToast],
  );

  const warning = useCallback(
    (message, options = {}) => {
      return addToast({
        type: 'warning',
        message,
        duration: 6000,
        ...options,
      });
    },
    [addToast],
  );

  const info = useCallback(
    (message, options = {}) => {
      return addToast({
        type: 'info',
        message,
        ...options,
      });
    },
    [addToast],
  );

  const value = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    toast,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className='toast-container'>
        {toasts.map(toastData => (
          <Toast key={toastData.id} {...toastData} onClose={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ToastProvider;
